import React, { useEffect, useRef, useState } from "react"
import { TranslateCookieHandler } from "@/components/Translate/google-translate"
import { Languages } from "lucide-react"
import i18n, { t, getSavedLanguage } from "@/i18n"
import { C, radius, shadow } from "@/theme/newDesignTokens"

interface Language {
    code: string
    name: string
    flag: string
}

export const languages: Language[] = [
    { code: "en", name: "English", flag: "🇬🇧" },
    { code: "de", name: "Deutsch", flag: "🇩🇪" },
    { code: "fr", name: "Français", flag: "🇫🇷" },
    { code: "es", name: "Español", flag: "🇪🇸" },
    { code: "it", name: "Italiano", flag: "🇮🇹" },
    { code: "sv", name: "Svenska", flag: "🇸🇪" },
    { code: "ja", name: "日本語", flag: "🇯🇵" },
    { code: "ar", name: "العربية", flag: "🇸🇦" },
]

export const LanguageSelector: React.FC = () => {
    const currentLangCode = getSavedLanguage()
    const currentLang = languages.find(l => l.code === currentLangCode) ?? languages[0]
    const [open, setOpen] = useState(false)
    const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null)
    const btnRef = useRef<HTMLButtonElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)

    const handleToggle = () => {
        if (!open && btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect()
            setMenuPos({ top: rect.bottom + 4, left: rect.left })
        }
        setOpen(prev => !prev)
    }

    const handleLanguageChange = async (lang: Language): Promise<void> => {
        setOpen(false)
        TranslateCookieHandler(`/auto/${lang.code}`)
        await i18n.changeLanguage(lang.code)
        window.location.reload()
    }

    useEffect(() => {
        if (!open) return
        const handleClickOutside = (e: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(e.target as Node) &&
                btnRef.current &&
                !btnRef.current.contains(e.target as Node)
            ) {
                setOpen(false)
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [open])

    return (
        <div className="notranslate" style={{ position: "relative" }}>
            <button
                ref={btnRef}
                onClick={handleToggle}
                title={t("Change language")}
                style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "4px 6px",
                    borderRadius: radius.sm,
                    color: C.navyDark,
                }}
            >
                <span style={{ fontSize: 13, fontWeight: 500, color: C.navyDark }}>{currentLang.flag} {currentLang.name}</span>
            </button>

            {open && menuPos && (
                <div
                    ref={menuRef}
                    className="notranslate"
                    style={{
                        position: "fixed",
                        top: menuPos.top,
                        left: menuPos.left,
                        background: C.white,
                        borderRadius: radius.md,
                        boxShadow: shadow.md,
                        border: `1px solid ${C.lightGray}`,
                        padding: "4px 0",
                        zIndex: 2100,
                        minWidth: 180,
                    }}
                >
                    {languages.map(lang => (
                        <button
                            key={lang.code}
                            onClick={() => handleLanguageChange(lang)}
                            className="notranslate"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                width: "100%",
                                padding: "8px 14px",
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                fontSize: 13,
                                color: C.navyDark,
                                textAlign: "left",
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = C.offWhite)}
                            onMouseLeave={e => (e.currentTarget.style.background = "none")}
                        >
                            <span style={{ fontSize: 18, lineHeight: 1 }}>{lang.flag}</span>
                            <span>{t(lang.name)}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}
